import { createContext, FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { AxiosError, AxiosResponse } from "axios";
import { useLocation } from "react-router-dom";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { setUser } from "@sentry/react";

import { RegistrationStatus, AuthStatus, UserDetailsType, UserPhoneEmail, BetaStatus } from "../../types";
import { dynamicConfig, localRoutes, getRequiredKeys, getBetaKeys, cdnUrl } from "../../config";
import { getFromLocalStorage, LocalStorageKeys, removeFromLocalStorage } from "../utils/localStorage";
import { getFullName, saveReferrerParam, saveUtmDataParams, formatDashedStringToCamelCase } from "../utils";
import api, { postRefreshToken } from "../api/api";
import { Analytics } from "../../analytics";
import { logOut } from "../api/helpers/apiFunctions/login";
import {
  getTokens,
  isAccessTokenExpired,
  removeUser,
  saveTokens,
  setAxiosToken,
  isUserGhost,
  isUserHost,
} from "../api/helpers/authorization";
import {
  getAppVersion,
  getMyUserDetails,
  getPhoneEmail,
  isActiveAccount,
  saveMyProfile,
} from "../api/helpers/apiFunctions/user";
import { authorization, bearer, increaseRetryCount, resetRetryCount, serverPath, shouldRetry } from "../api/config";
import { useSetIgnoreOnboarding } from "../data";
import { isLocalHost } from "../utils/isLocalHost";
import { reportError } from "../utils/reportError";
import { initIntercom } from "../utils/intercom";

export interface UserContextType {
  isGhost: boolean;
  isHost: boolean;
  isBeta: boolean;
  isActivated: boolean | null;
  betaStatus: BetaStatus;
  userDetails: Partial<UserDetailsType>;
  getUserInformation: () => Promise<UserDetailsType>;
  logout: (withApi?: boolean) => Promise<void>;
  startRefreshToken: () => void;
  userCompletedRegistration: RegistrationStatus;
  userAuthStatus: AuthStatus;
  loadApp: () => Promise<void>;
  personalDetails?: UserPhoneEmail;
  getPersonalInformation: () => Promise<UserPhoneEmail>;
}

let refreshTokenTimeout: NodeJS.Timeout;

export const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isBeta, setIsBeta] = useState(false);
  const [isActivated, setIsActivated] = useState<boolean | null>(null);
  const [betaStatus, setBetaStatus] = useState(BetaStatus.LOADING);
  const [isGhost, setIsGhost] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetailsType>();
  const [personalDetails, setPersonalDetails] = useState<UserPhoneEmail>();
  const [userAuthStatus, setUserAuthStatus] = useState(AuthStatus.LOADING);
  const [userCompletedRegistration, setUserCompletedRegistration] = useState(RegistrationStatus.LOADING);

  const logout = useCallback(async (withApi = false) => {
    if (withApi) {
      await logOut().catch(() => console.log("logout api call error with no effect in the client"));
    }

    removeUser();

    if (dynamicConfig.landingUrl) {
      document.cookie = `loggedIn=false; domain=${dynamicConfig.landingUrl}`;
    }

    setUserAuthStatus(AuthStatus.NOT_AUTHENTICATED);
    setUserCompletedRegistration(RegistrationStatus.NOT_FINISH);
    setBetaStatus(BetaStatus.NOT_STARTED);
  }, []);

  const refreshAndSaveTokens = useCallback(async () => {
    try {
      const { data } = await postRefreshToken();

      saveTokens(data);
      setIsGhost(isUserGhost(data.accessToken));
      setIsHost(isUserHost(data.accessToken));

      return data;
    } catch (error) {
      logout();
    }

    return undefined;
  }, []); // eslint-disable-line

  const startRefreshToken = useCallback(async () => {
    clearTimeout(refreshTokenTimeout);
    const { expireIn, accessToken, refreshToken } = getTokens();

    let delay = expireIn - Date.now();
    if (delay < 0) delay = 0;

    refreshTokenTimeout = setTimeout(async () => {
      if (accessToken && refreshToken) {
        await refreshAndSaveTokens();
        startRefreshToken();
      }
    }, delay);
  }, [refreshAndSaveTokens]);

  const client = useLDClient();
  const setIgnoreOnboarding = useSetIgnoreOnboarding();

  const getUserInformation = useCallback(
    () =>
      getMyUserDetails().then(async ({ data: responseData }) => {
        const data: UserDetailsType = {
          ...responseData,
        };

        if (responseData.firstName || responseData.lastName) {
          data.fullname = getFullName(responseData);
        }

        setUserDetails(data);
        setUserAuthStatus(AuthStatus.AUTHENTICATED);
        const isAlphaOrBetaUser = ["ALPHA", "BETA"].includes(data?.appVersion);

        if (client) {
          const defaultUser = client.getUser();
          const user = {
            key: String(data.userId),
            name: data.fullname,
            custom: {
              id: Number(data.userId),
              appVersion: data.appVersion,
            } as { [key: string]: string | number },
          };

          const registrationExperiment = getFromLocalStorage(LocalStorageKeys.registrationExperiment);
          if (registrationExperiment) {
            user.custom["Registration Screen Experiment"] = registrationExperiment;
            removeFromLocalStorage(LocalStorageKeys.registrationExperiment);
          }

          const flags = await client.identify(user);
          client.alias(user, defaultUser);

          Object.entries(flags).forEach(([key, value]) => {
            const formattedKey = formatDashedStringToCamelCase(key);
            dynamicConfig.features.flags[formattedKey] = value;

            if (formattedKey === "bioScreenFg") {
              const defaultValueThatShouldBeIgnored = -1;

              if (value !== defaultValueThatShouldBeIgnored) {
                Analytics.setUserProperties({ "BIO EXPERIMENT": value });
              }
            }

            if (formattedKey === "hideOnboardingStepsFg") {
              const defaultValueThatShouldBeIgnored = -1;

              if (value > defaultValueThatShouldBeIgnored) {
                const hideOnboarding = value === 1;

                Analytics.setUserProperties({ "HIDE ONBOARDING EXPERIMENT": hideOnboarding });
                if (responseData.showOnboarding) {
                  setIgnoreOnboarding(hideOnboarding);
                  if (hideOnboarding) {
                    saveMyProfile({ ...data, showOnboarding: false });
                  }
                } else {
                  setIgnoreOnboarding(true);
                }
              } else {
                setIgnoreOnboarding(!responseData.showOnboarding);
              }
            }
          });

          if (getRequiredKeys(data?.country, isAlphaOrBetaUser).some((prop) => !data?.[prop])) {
            setUserCompletedRegistration(RegistrationStatus.NOT_FINISH);
          } else if (getRequiredKeys(data?.country, isAlphaOrBetaUser).every((prop) => data?.[prop])) {
            setUserCompletedRegistration(RegistrationStatus.FINISH);
          }
        } else {
          setIgnoreOnboarding(!responseData.showOnboarding);

          if (getRequiredKeys(data?.country, isAlphaOrBetaUser).some((prop) => !data?.[prop])) {
            setUserCompletedRegistration(RegistrationStatus.NOT_FINISH);
          } else if (getRequiredKeys(data?.country, isAlphaOrBetaUser).every((prop) => data?.[prop])) {
            setUserCompletedRegistration(RegistrationStatus.FINISH);
          }
        }

        return data;
      }),
    [client] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const getPersonalInformation = useCallback(
    () =>
      getPhoneEmail().then(({ data }) => {
        setPersonalDetails(data);

        return data;
      }),
    []
  );

  const loadApp = useCallback(async () => {
    if (!client && dynamicConfig.isProduction) return;

    setUserAuthStatus(AuthStatus.LOADING);
    setUserCompletedRegistration(RegistrationStatus.LOADING);
    // This is used to get initial user value when website loads
    const { accessToken, refreshToken } = getTokens();
    const isTester = getFromLocalStorage(LocalStorageKeys.isTester);

    if (!accessToken || (!refreshToken && !isTester)) {
      logout();
      return;
    }

    if (isAccessTokenExpired()) {
      // maybe add refresh token to every page load regardless of expire time
      await refreshAndSaveTokens();
    } else {
      setAxiosToken(accessToken);
      setIsGhost(isUserGhost(accessToken));
      setIsHost(isUserHost(accessToken));
    }

    const details = await getUserInformation();
    if (details?.userId) {
      try {
        // Sentry identify user
        setUser({
          id: String(details.userId),
          username: details.fullname,
        });
      } catch (e) {
        console.error("Sentry setUser ERROR:", e);
      }

      if (window.location.pathname.includes(localRoutes.accountSetupSpecify)) {
        Analytics.alias(String(details.userId));
        Analytics.identify(String(details.userId), { name: details.fullname }, { mixPanel: false });
      } else {
        Analytics.identify(String(details.userId), { name: details.fullname });
      }

      const appVersion = await getAppVersion();
      const photoUrl = details.profilePhotoUrl ? cdnUrl + details.profilePhotoUrl : undefined;
      Analytics.setUserProperties({
        user_id: String(details.userId),
        $avatar: photoUrl,
        "APP BUILD VERSION": appVersion,
      });
    }

    if (!isLocalHost || process.env.NODE_ENV === "development") {
      const personalData = await getPersonalInformation();
      initIntercom(details.userId, details.fullname, personalData.email);
    }

    let isActive = true;
    if (!isLocalHost || process.env.NODE_ENV === "development") {
      const response = await isActiveAccount().catch((err) =>
        reportError("UserContext.tsx - loadApp.isActiveAccount.catch", "Check isActiveAccount Error", err)
      );
      if (typeof response?.data === "boolean") {
        isActive = response.data;
      }
    }

    setIsActivated(isActive);

    if (details?.appVersion === "BETA" && !isActive) {
      const personalData = await getPersonalInformation();

      const data = { ...details, ...personalData };

      if (getBetaKeys().every((prop) => !!data[prop])) {
        setBetaStatus(BetaStatus.FINISH);
      } else if (getBetaKeys().some((prop) => !!data[prop])) {
        setBetaStatus(BetaStatus.NOT_FINISH);
      } else {
        setBetaStatus(BetaStatus.NOT_STARTED);
      }

      setIsBeta(true);
    } else {
      setBetaStatus(BetaStatus.NOT_STARTED);
    }

    if (dynamicConfig.landingUrl) {
      document.cookie = `loggedIn=true; domain=${dynamicConfig.landingUrl}`;
    }

    if (!isTester) {
      startRefreshToken();
    }
  }, [client]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.interceptors.response.use(
      (response: AxiosResponse) => {
        if (response.config.url) {
          resetRetryCount(response.config.url);
        }

        return response;
      },
      async (error: AxiosError) => {
        reportError(
          "UserContext.tsx - HTTPS Request Error",
          `Status: ${error.response?.status} - URL: "${error.config.url}"`,
          error
        );

        if (
          error.message.toLocaleLowerCase() === "network error" &&
          error.config.url &&
          shouldRetry(error.config.url)
        ) {
          await increaseRetryCount(error.config.url);

          return api.request(error.config);
        }

        if (error.config.url === serverPath.refresh) {
          await logout();

          return Promise.reject(error);
        }

        const isTester = getFromLocalStorage(LocalStorageKeys.isTester);

        if (isTester && (error.response?.status === 401 || error.response?.status === 403)) {
          await logout();

          return Promise.reject(error);
        }

        const shouldNotRepeatUrls = [serverPath.logOut, serverPath.utm];
        if (shouldNotRepeatUrls.includes(error.config.url || "") || isTester) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401) {
          const tokens = await refreshAndSaveTokens();

          if (!tokens) return Promise.reject(error);

          if (!error.config.headers) {
            error.config.headers = {};
          }

          error.config.headers[authorization] = `${bearer} ${tokens?.accessToken}`;
          console.log(error.config.url, "tries again with new accessToken");

          return api.request(error.config);
        }

        return Promise.reject(error);
      }
    );
  }, []); // eslint-disable-line

  useEffect(() => {
    loadApp();
  }, [loadApp]);

  useEffect(() => {
    removeFromLocalStorage(LocalStorageKeys.UtmData);
    removeFromLocalStorage(LocalStorageKeys.referrer);
    removeFromLocalStorage(LocalStorageKeys.showPermissionDialog);
    removeFromLocalStorage(LocalStorageKeys.reloadPageIfNeeded);
    removeFromLocalStorage(LocalStorageKeys.invitationSentBy);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { search } = useLocation();
  useEffect(() => {
    // This is used to get utm data and save it to local storage
    saveUtmDataParams(search);
    saveReferrerParam(search);

    // This is used to get utm data and save it to Analytics
    const searchParams = new URLSearchParams(search);
    const utmKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

    const utmLastValues = {} as { [key: string]: string };
    const utmFirstValues = {} as { [key: string]: string };

    utmKeys.forEach((key) => {
      const value = searchParams.get(key);

      if (value) {
        utmLastValues[`${key} [Last Visit]`] = value;
        utmFirstValues[`${key} [First Visit]`] = value;
      }
    });

    if (Object.keys(utmLastValues).length) {
      Analytics.setUserProperties(utmLastValues);
      Analytics.setUserPropertiesOnce(utmFirstValues);
      Analytics.setSuperProperties(utmLastValues);
    }
  }, [search]);

  const value: UserContextType = useMemo(
    () => ({
      userDetails: userDetails || {},
      getUserInformation,
      logout,
      userCompletedRegistration,
      isGhost,
      startRefreshToken,
      userAuthStatus,
      loadApp,
      personalDetails,
      getPersonalInformation,
      isBeta,
      betaStatus,
      isActivated,
      isHost,
    }),
    [
      getUserInformation,
      userDetails,
      logout,
      isGhost,
      userCompletedRegistration,
      startRefreshToken,
      userAuthStatus,
      loadApp,
      personalDetails,
      getPersonalInformation,
      isBeta,
      betaStatus,
      isActivated,
      isHost,
    ]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
